import { jsPDF } from 'jspdf'
import { LIBRARY_HASH, PACKAGE_ID, PACKAGE_VERSION, scenarios } from '../data/ledger'
import {
  ChatMessage,
  FlagRecord,
  PackConfidence,
  Reflection,
  ScenarioResult,
} from '../types/assessment'
import { EVIDENCE_KEY, formatDuration } from './scoring'

export interface RecordPdfInput {
  participantId: string
  sessionId: string
  elapsedSeconds: number
  promptCount: number
  dwell: { reconciliation: number; budget: number; documents: number }
  openedDocs: string[]
  results: ScenarioResult[]
  flags: Record<string, FlagRecord>
  reflection: Reflection
  packConfidence: PackConfidence | null
  messages: ChatMessage[]
}

const MARGIN = 54

const CONFIDENCE_LABEL: Record<PackConfidence, string> = {
  yes: 'Yes — ready to submit',
  'with-flags': 'With the flags noted',
  no: 'No, material issues remain',
}

export function downloadRecordPdf(input: RecordPdfInput) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const width = doc.internal.pageSize.getWidth()
  const height = doc.internal.pageSize.getHeight()
  const maxWidth = width - MARGIN * 2
  let y = MARGIN

  const line = (text: string, size = 10, style: 'normal' | 'bold' = 'normal', gap = 13) => {
    doc.setFont('helvetica', style)
    doc.setFontSize(size)
    const wrapped = doc.splitTextToSize(text, maxWidth) as string[]
    wrapped.forEach((w) => {
      doc.text(w, MARGIN, y)
      y += gap
    })
  }

  const heading = (text: string) => {
    y += 8
    line(text.toUpperCase(), 8.5, 'bold', 12)
    doc.setDrawColor(203, 213, 225)
    doc.line(MARGIN, y - 4, width - MARGIN, y - 4)
    y += 6
  }

  const footer = () => {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(148, 163, 184)
    doc.text(
      `${PACKAGE_ID} ${PACKAGE_VERSION} · library ${LIBRARY_HASH} · participant ${input.participantId || '—'}`,
      MARGIN,
      height - 28,
    )
    doc.setTextColor(15, 23, 42)
  }

  // ---- Page 1
  doc.setFillColor(15, 23, 42)
  doc.rect(0, 0, width, 82, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.text('Participant record of evidence', MARGIN, 42)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Lentic Assessment · European entity close · Q2 2026', MARGIN, 60)
  doc.setTextColor(15, 23, 42)
  y = 112

  line(`Participant ${input.participantId || '—'} · session ${input.sessionId}`, 11, 'bold', 16)
  line(`Generated ${new Date().toLocaleString()}`, 9, 'normal', 16)
  line(
    'This is a record of what happened, not a verdict. A named human reviews it before any conclusion is drawn.',
    9,
  )

  heading('Session')
  line(`Time on assessment: ${formatDuration(input.elapsedSeconds)}`)
  line(`Prompts to the Close Assistant: ${input.promptCount}`)
  line(
    `Documents opened (${input.openedDocs.length} of 7): ${
      input.openedDocs.join(', ') || 'none'
    }`,
  )
  line(
    `Tab time — source documents ${formatDuration(input.dwell.documents)} · reconciliation ${formatDuration(
      input.dwell.reconciliation,
    )} · P&L ${formatDuration(input.dwell.budget)}`,
  )
  line(
    `Whole-pack sign-off: ${input.packConfidence ? CONFIDENCE_LABEL[input.packConfidence] : '—'}`,
  )

  heading('Scenario-by-scenario record')
  input.results.forEach((r) => {
    const spec = scenarios.find((s) => s.id === r.scenarioId)
    if (!spec) return
    line(`${r.scenarioId} · ${spec.klass} · ${spec.rowId}`, 10, 'bold', 12)
    line(EVIDENCE_KEY[r.category].label, 9.5, 'bold', 12)
    line(r.evidence, 9)
    y += 3
  })

  footer()

  // ---- Page 2
  doc.addPage()
  y = MARGIN
  line('Participant entries and reflection', 13, 'bold', 20)

  heading('Flags and corrections')
  const entries = Object.values(input.flags)
  if (entries.length === 0) {
    line('— no entries recorded —', 10)
  } else {
    entries.forEach((f) => {
      line(`${f.rowId} · ${f.kind === 'correction' ? 'Correction' : 'Flag'} · ${f.actionType}`, 10, 'bold', 12)
      line(f.what, 9)
      line(`Sources cited: ${f.sources.join(', ') || 'none'} · sign-off: ${f.signOff}`, 8.5)
      if (f.narrative) line(`Narrative: ${f.narrative}`, 8.5)
      if (f.escalateWhy) line(`Escalation: to ${f.escalateTo || '—'} — ${f.escalateWhy}`, 8.5)
      y += 3
    })
  }

  heading('1. What did you check against source documents?')
  line(input.reflection.q1.trim() || '— no answer —', 9.5)

  heading('2. What did you accept without checking, and why?')
  line(input.reflection.q2.trim() || '— no answer —', 9.5)

  heading('3. What would you verify next with 30 more minutes?')
  line(input.reflection.q3.trim() || '— no answer —', 9.5)

  heading('Assistant prompts (verbatim)')
  const prompts = input.messages.filter((m) => m.role === 'user')
  if (prompts.length === 0) {
    line('— the assistant was never used —', 9.5)
  } else {
    prompts.slice(0, 14).forEach((p, i) => {
      line(`${i + 1}. ${p.content.replace(/\s+/g, ' ').slice(0, 220)}`, 8.5, 'normal', 11)
    })
  }

  footer()

  doc.save(`lentic-${PACKAGE_ID}-${input.participantId || 'participant'}-record.pdf`)
}
